<template>
  <div class="space-y-6">
    <!-- Выбор категории блока по иконкам -->
    <div v-if="!isCategoryLocked">
      <div class="text-lg font-semibold mb-4">Выберите тип блока</div>
      
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button
          v-for="category in blockCategories"
          :key="category.id"
          @click="selectCategory(category.id)"
          :class="[
            'flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all',
            selectedCategoryId === category.id
              ? 'border-accent bg-accent bg-opacity-10'
              : 'border-gray-700 hover:border-gray-600 bg-secondary-bg'
          ]"
        >
          <div class="mb-2">
            <img 
              :src="require(`../../assets/icons/${category.icon}`)" 
              :alt="category.name"
              class="w-16 h-16 opacity-80"
              style="filter: invert(1);"
            />
          </div>
          <div class="text-sm font-medium text-center">{{ category.name }}</div>
        </button>
      </div>
    </div>
    
    <!-- Отображение выбранной категории (только для чтения) -->
    <div v-else-if="selectedCategory" class="mb-4 p-4 bg-gray-100 dark:bg-secondary-bg rounded-lg flex items-center">
      <div class="mr-4">
        <img 
          :src="require(`../../assets/icons/${selectedCategory.icon}`)" 
          :alt="selectedCategory.name"
          class="w-10 h-10 opacity-80 dark-icon"
        />
      </div>
      <div class="flex-1">
        <div class="text-sm text-gray-500 dark:text-gray-400 mb-1">Тип блока</div>
        <div class="text-lg font-semibold text-gray-900 dark:text-primary">{{ selectedCategory.name }}</div>
        <div class="text-xs text-gray-600 dark:text-gray-500 mt-1">{{ selectedCategory.description }}</div>
      </div>
    </div>

    <!-- Параметры категории -->
    <div v-if="selectedCategory && selectedCategory.parameters" class="space-y-4">
      <div class="text-lg font-semibold">Параметры блока</div>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div 
          v-for="param in visibleParameters" 
          :key="param.key" 
          :class="param.type === 'checkbox' ? 'col-span-full' : ''"
        >
          <!-- Checkbox параметры -->
          <div v-if="param.type === 'checkbox'" class="flex items-center p-3 bg-gray-100 dark:bg-secondary-bg rounded-lg">
            <input 
              type="checkbox" 
              :id="`param-${param.key}`"
              v-model="categoryParameters[param.key]" 
              class="w-5 h-5 text-accent bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-accent focus:ring-2"
              @change="onParameterChange"
            />
            <label :for="`param-${param.key}`" class="ml-3 text-sm font-medium cursor-pointer text-gray-900 dark:text-primary">
              {{ param.label }}
            </label>
          </div>
          
          <!-- Select параметры -->
          <div v-else>
            <label class="block text-sm mb-2 text-gray-700 dark:text-gray-300">{{ param.label }}</label>
            <select 
              v-model="categoryParameters[param.key]" 
              class="w-full bg-white dark:bg-secondary-bg p-3 rounded-lg border border-gray-300 dark:border-gray-700 focus:border-accent focus:outline-none text-gray-900 dark:text-primary"
              @change="onParameterChange"
            >
              <option value="" disabled>{{ param.required ? 'Выберите...' : 'Опционально...' }}</option>
              <option 
                v-for="option in getVisibleOptions(param)" 
                :key="option.value" 
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Поля блока -->
    <div v-if="currentFields && currentFields.length > 0" class="space-y-4">
      <div class="text-lg font-semibold text-gray-900 dark:text-primary">Содержимое блока</div>
      
      <div class="space-y-4">
        <div v-for="field in currentFields" :key="field.key">
          <label class="block text-sm mb-2 text-gray-700 dark:text-gray-300">
            {{ field.label }}
            <span v-if="field.required" class="text-red-500 dark:text-red-400">*</span>
          </label>
          
          <!-- Специальная обработка для кнопок -->
          <div 
            v-if="field.key === 'button.action'" 
            :class="[
              'space-y-3 p-4 rounded-lg',
              isFieldMissing(field.key) || isFieldMissing('button.link') ? 'bg-secondary-bg border-2 border-red-500' : 'bg-secondary-bg'
            ]"
          >
            <div class="space-y-2">
              <label class="flex items-center space-x-3 cursor-pointer">
                <input 
                  type="radio" 
                  v-model="buttonActionType" 
                  value="link" 
                  name="buttonAction"
                  class="w-4 h-4 text-accent"
                />
                <span>Перейти по ссылке</span>
              </label>
              <label class="flex items-center space-x-3 cursor-pointer">
                <input 
                  type="radio" 
                  v-model="buttonActionType" 
                  value="form" 
                  name="buttonAction"
                  class="w-4 h-4 text-accent"
                />
                <span>Открыть форму заявки</span>
              </label>
            </div>
            
            <div v-if="buttonActionType === 'link'" class="mt-3">
              <input 
                v-model="buttonLink" 
                type="url" 
                :class="[
                  'w-full bg-background p-3 rounded-lg border focus:border-accent focus:outline-none',
                  isFieldMissing('button.link') ? 'border-red-500 border-2' : 'border-gray-700'
                ]"
                placeholder="https://example.com"
              />
            </div>
            
            <div v-if="isFieldMissing(field.key) || isFieldMissing('button.link')" class="text-red-400 text-sm">
              ⚠ {{ isFieldMissing('button.link') ? 'Укажите ссылку' : 'Выберите действие кнопки' }}
            </div>
          </div>
          
          <!-- Поля для загрузки медиа -->
          <div v-else-if="field.type === 'media' || isMediaKey(field.key)">
            <div :class="isFieldMissing(field.key) ? 'border-2 border-red-500 rounded-lg p-3' : ''">
              <FileUploadField
                :label="`Загрузить ${field.key.includes('image') ? 'изображение' : 'видео'}`"
                :accept="field.key.includes('image') ? 'image/*' : 'video/*'"
                :acceptText="field.key.includes('image') ? 'Изображения (JPG, PNG, GIF)' : 'Видео (MP4, AVI, MOV)'"
                @file-uploaded="(response) => handleMediaUpload(field.key, response)"
              />
              <div v-if="isFieldMissing(field.key)" class="mt-2 text-red-400 text-sm">
                ⚠ Обязательное поле
              </div>
            </div>
            <div v-if="getValueByPath(fieldValues, field.key)" class="mt-2 p-3 bg-gray-100 dark:bg-secondary-bg rounded-lg text-sm">
              <span class="text-gray-600 dark:text-gray-400">Файл загружен:</span> 
              <span class="text-accent">{{ getValueByPath(fieldValues, field.key) }}</span>
            </div>
          </div>
          
          <!-- Поля textarea -->
          <textarea 
            v-else-if="field.type === 'text'" 
            :value="getNestedValue(fieldValues, field.key)"
            @input="handleFieldInput(field.key, $event.target.value)"
            :class="[
              'w-full bg-secondary-bg p-3 rounded-lg border focus:border-accent focus:outline-none min-h-[120px]',
              isFieldMissing(field.key) ? 'border-red-500 border-2' : 'border-gray-700'
            ]"
            :placeholder="field.label"
          />
          
          <!-- Обычные текстовые поля -->
          <input 
            v-else 
            type="text" 
            :value="getNestedValue(fieldValues, field.key)"
            @input="handleFieldInput(field.key, $event.target.value)"
            :class="[
              'w-full bg-secondary-bg p-3 rounded-lg border focus:border-accent focus:outline-none',
              isFieldMissing(field.key) ? 'border-red-500 border-2' : 'border-gray-700'
            ]"
            :placeholder="field.label"
          />
        </div>
      </div>
    </div>

    <!-- Коллекции (для галерей, FAQ и т.д.) -->
    <div v-if="currentCollection" class="space-y-4">
      <div class="flex items-center justify-between">
        <div class="text-lg font-semibold">{{ currentCollection.label }}</div>
        <button 
          class="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover transition-colors"
          @click="addCollectionItem"
        >
          + Добавить элемент
        </button>
      </div>
      
      <div class="text-sm text-gray-400">
        Количество элементов: {{ localCollection.length }}
      </div>
      
      <div class="space-y-4">
        <div v-for="(item, idx) in localCollection" :key="idx" class="p-4 rounded-lg bg-gray-100 dark:bg-secondary-bg border border-gray-300 dark:border-gray-700">
          <div class="space-y-3">
            <div v-for="cf in currentCollection.itemFields" :key="cf.key">
              <label class="block text-sm mb-2 text-gray-300">
                {{ cf.label }}
                <span v-if="cf.required" class="text-red-400">*</span>
              </label>
              
              <!-- Поля для загрузки медиа в коллекциях -->
              <div v-if="cf.type === 'media' || isMediaKey(cf.key)">
                <FileUploadField
                  :label="`Загрузить ${cf.key.includes('image') ? 'изображение' : 'видео'}`"
                  :accept="cf.key.includes('image') ? 'image/*' : 'video/*'"
                  :acceptText="cf.key.includes('image') ? 'Изображения (JPG, PNG, GIF)' : 'Видео (MP4, AVI, MOV)'"
                  @file-uploaded="(response) => handleCollectionMediaUpload(idx, cf.key, response)"
                />
                <div v-if="localCollection[idx][cf.key]" class="mt-2 p-3 bg-white dark:bg-background rounded-lg text-sm">
                  <span class="text-gray-600 dark:text-gray-400">Файл загружен:</span>
                  <span class="text-accent">{{ localCollection[idx][cf.key] }}</span>
                </div>
              </div>
              
              <!-- Поля textarea -->
              <textarea 
                v-else-if="cf.type === 'text'" 
                v-model="localCollection[idx][cf.key]" 
                class="w-full bg-white dark:bg-background p-3 rounded-lg border border-gray-300 dark:border-gray-600 focus:border-accent focus:outline-none min-h-[100px] text-gray-900 dark:text-primary"
                :placeholder="cf.label"
              />
              
              <!-- Обычные текстовые поля -->
              <input 
                v-else 
                type="text" 
                v-model="localCollection[idx][cf.key]" 
                class="w-full bg-white dark:bg-background p-3 rounded-lg border border-gray-300 dark:border-gray-600 focus:border-accent focus:outline-none text-gray-900 dark:text-primary"
                :placeholder="cf.label"
              />
            </div>
          </div>
          <div class="mt-4 text-right">
            <button 
              class="px-4 py-2 text-red-400 hover:text-red-300 transition-colors"
              @click="removeCollectionItem(idx)"
            >
              🗑 Удалить элемент
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Кнопки действий -->
    <div class="pt-4 border-t border-gray-700 flex items-center justify-between">
      <div v-if="finalBlockType" class="text-sm">
        <span class="text-gray-400">Тип блока:</span>
        <span 
          class="ml-2 px-3 py-1 rounded font-mono"
          :class="isValidBlockType ? 'bg-secondary-bg text-accent' : 'bg-red-900 bg-opacity-30 text-red-400'"
        >
          {{ finalBlockType }}
        </span>
        <span v-if="!isValidBlockType" class="ml-2 text-xs text-red-400">⚠ Тип не существует</span>
      </div>
      <div v-else class="text-sm text-gray-500">
        Выберите параметры блока
      </div>
      <div class="flex items-center space-x-2">
        <button class="px-4 py-2 rounded bg-gray-200 dark:bg-secondary-bg hover:bg-gray-300 dark:hover:bg-secondary-bg-darker transition-colors text-gray-900 dark:text-primary" @click="$emit('cancel')">Отмена</button>
        <button 
          class="px-4 py-2 rounded text-white transition-colors" 
          :class="canSubmit && isValidBlockType ? 'bg-accent hover:bg-accent-hover' : 'bg-gray-600 hover:bg-gray-500'"
          @click="submit"
        >
          Сохранить
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getBlockConfigByType, getBlockCategories, VALID_BLOCK_TYPES } from './blockTypes';
import FileUploadField from '../FileUploadField.vue';

export default {
  name: 'BlogBlockForm',
  components: {
    FileUploadField
  },
  props: {
    value: { type: Object, default: () => ({}) },
    type: { type: String, default: '' },
    position: { type: Number, default: null },
    mode: { type: String, default: 'create' },
    initialCategoryId: { type: String, default: null },
  },
  data() {
    return {
      selectedCategoryId: '',
      selectedCategory: null,
      categoryParameters: {},
      localPosition: this.position,
      payload: JSON.parse(JSON.stringify(this.value?.payload || {})),
      blockCategories: getBlockCategories(),
      fieldValues: {},
      localCollection: [],
      buttonActionType: 'link',
      buttonLink: '',
      currentFields: [],
      currentCollection: null,
      finalBlockType: '',
      attemptedSubmit: false
    }
  },
  computed: {
    isCategoryLocked() {
      // Категория заблокирована при создании блока с предустановленной категорией
      // или при редактировании существующего блока
      return (this.initialCategoryId && this.mode === 'create') || (this.mode === 'edit');
    },
    visibleParameters() {
      if (!this.selectedCategory || !this.selectedCategory.parameters) return [];
      
      return this.selectedCategory.parameters.filter(param => {
        // Параметр всегда видим, если нет условия showWhen
        if (!param.showWhen) return true;
        
        // Специальная обработка для contentRatio (видно только для изображений)
        if (param.key === 'contentRatio') {
          const mediaType = this.categoryParameters.mediaType;
          const fullWidth = this.categoryParameters.fullWidth;
          const alignment = this.categoryParameters.alignment;
          return mediaType === 'image' && !fullWidth && alignment !== 'media-only';
        }
        
        // Для остальных - всегда показываем
        return true;
      });
    },
    isValidBlockType() {
      if (!this.finalBlockType) return true;
      return VALID_BLOCK_TYPES.has(this.finalBlockType);
    },
    canSubmit() {
      if (!this.selectedCategory) return false;
      
      // Проверяем обязательные параметры
      if (this.selectedCategory.parameters) {
        const requiredParams = this.selectedCategory.parameters.filter(p => p.required);
        if (!requiredParams.every(p => this.categoryParameters[p.key])) {
          return false;
        }
      }
      
      // Проверяем обязательные поля
      if (this.currentFields) {
        const requiredFields = this.currentFields.filter(f => f.required);
        if (!requiredFields.every(f => {
          // Специальная обработка для кнопок
          if (f.key === 'button.action') {
            if (!this.buttonActionType) return false;
            if (this.buttonActionType === 'link' && !this.buttonLink) return false;
            return true;
          }
          
          const value = this.getNestedValue(this.fieldValues, f.key);
          // Также проверяем альтернативное имя поля с _file_id для медиа-файлов
          if (!value && f.key.endsWith('_url')) {
            const fileIdKey = f.key.replace('_url', '_file_id');
            return this.getNestedValue(this.fieldValues, fileIdKey);
          }
          return value;
        })) {
          return false;
        }
      }
      
      return true;
    },
    missingFields() {
      const missing = [];
      
      // Проверяем обязательные параметры
      if (this.selectedCategory?.parameters) {
        const requiredParams = this.selectedCategory.parameters.filter(p => p.required);
        requiredParams.forEach(param => {
          if (!this.categoryParameters[param.key]) {
            missing.push({ type: 'parameter', key: param.key, label: param.label });
          }
        });
      }
      
      // Проверяем обязательные поля
      if (this.currentFields) {
        const requiredFields = this.currentFields.filter(f => f.required);
        requiredFields.forEach(field => {
          // Специальная обработка для кнопок
          if (field.key === 'button.action') {
            // Проверяем buttonActionType
            if (!this.buttonActionType) {
              missing.push({ type: 'field', key: field.key, label: field.label });
            } else if (this.buttonActionType === 'link' && !this.buttonLink) {
              missing.push({ type: 'field', key: 'button.link', label: 'Ссылка' });
            }
            return;
          }
          
          const value = this.getNestedValue(this.fieldValues, field.key);
          // Также проверяем альтернативное имя поля с _file_id для медиа-файлов
          if (!value) {
            if (field.key.endsWith('_url')) {
              const fileIdKey = field.key.replace('_url', '_file_id');
              if (!this.getNestedValue(this.fieldValues, fileIdKey)) {
                missing.push({ type: 'field', key: field.key, label: field.label });
              }
            } else {
              missing.push({ type: 'field', key: field.key, label: field.label });
            }
          }
        });
      }
      
      return missing;
    }
  },
  watch: {
    type(newVal) {
      if (newVal) {
        // Для существующих блоков пытаемся определить категорию
        this.initializeFromExistingType(newVal);
      }
    },
    value(newVal) {
      this.payload = JSON.parse(JSON.stringify(newVal?.payload || {}));
      this.updateFieldValues();
    },
    initialCategoryId(newVal) {
      if (newVal && this.mode === 'create' && !this.selectedCategoryId) {
        this.selectedCategoryId = newVal;
        this.onCategoryChange();
      }
    },
    fieldValues: {
      handler() {
        // Сбрасываем флаг попытки сабмита при изменении полей
        if (this.attemptedSubmit) {
          this.attemptedSubmit = false;
        }
      },
      deep: true
    },
    buttonActionType() {
      // Сбрасываем флаг при изменении типа действия кнопки
      if (this.attemptedSubmit) {
        this.attemptedSubmit = false;
      }
    },
    buttonLink() {
      // Сбрасываем флаг при изменении ссылки кнопки
      if (this.attemptedSubmit) {
        this.attemptedSubmit = false;
      }
    }
  },
  mounted() {
    // Автоматически устанавливаем категорию если она задана при создании
    if (this.initialCategoryId && this.mode === 'create') {
      this.selectedCategoryId = this.initialCategoryId;
      this.onCategoryChange();
    }
    
    // При редактировании определяем категорию из типа блока
    if (this.mode === 'edit' && this.type) {
      // Сначала загружаем payload
      this.payload = JSON.parse(JSON.stringify(this.value?.payload || {}));
      // Затем определяем категорию
      this.initializeFromExistingType(this.type);
    }
  },
  methods: {
    isMediaKey(key) {
      return key.endsWith('image_url') || key.endsWith('video_url');
    },
    selectCategory(categoryId) {
      this.selectedCategoryId = categoryId;
      this.onCategoryChange();
    },
    getVisibleOptions(param) {
      if (!param.options) return [];
      
      const mediaType = this.categoryParameters.mediaType;
      const fullWidth = this.categoryParameters.fullWidth;
      
      return param.options.filter(option => {
        // Фильтруем опции для hero блоков (fullWidth = true)
        if (fullWidth) {
          // Для hero блоков - только text-left, text-center и media-only
          if (param.key === 'alignment') {
            // Скрываем опции только для изображений
            if (option.imageOnly) return false;
            // Показываем только базовые и hero-опции
            return ['text-left', 'text-center', 'media-only'].includes(option.value);
          }
        }
        
        // Фильтруем опции "только для изображений" если выбрано видео
        if (mediaType === 'video' && option.imageOnly) {
          return false;
        }
        
        // Фильтруем опции "только для hero" если не fullWidth
        if (!fullWidth && option.heroOnly) {
          return false;
        }
        
        return true;
      });
    },
    onCategoryChange() {
      if (!this.selectedCategoryId) {
        this.selectedCategory = null;
        this.categoryParameters = {};
        this.currentFields = [];
        this.currentCollection = null;
        this.finalBlockType = '';
        return;
      }
      
      this.selectedCategory = this.blockCategories.find(c => c.id === this.selectedCategoryId);
      this.categoryParameters = {};
      this.currentFields = [];
      this.currentCollection = null;
      this.finalBlockType = '';
      
      // Инициализируем параметры по умолчанию
      if (this.selectedCategory.parameters) {
        this.selectedCategory.parameters.forEach(param => {
          if (param.type === 'checkbox') {
            // Для checkbox используем default или false
            this.categoryParameters[param.key] = param.default !== undefined ? param.default : false;
          } else if (param.options && param.options.length > 0) {
            // Для select используем default или первый вариант
            this.categoryParameters[param.key] = param.default !== undefined ? param.default : param.options[0].value;
          } else if (param.default !== undefined) {
            // Если есть default, используем его
            this.categoryParameters[param.key] = param.default;
          }
        });
      }
      
      this.updateFieldsAndCollection();
    },
    onParameterChange() {
      // Сбрасываем флаг попытки сабмита при изменении параметров
      this.attemptedSubmit = false;
      
      // Автопереключение режима кнопки по параметру hasButton
      if (this.categoryParameters.hasButton === 'link') this.buttonActionType = 'link';
      if (this.categoryParameters.hasButton === 'form') this.buttonActionType = 'form';
      
      // Проверяем, что текущее выравнивание все еще доступно
      const alignmentParam = this.selectedCategory?.parameters?.find(p => p.key === 'alignment');
      if (alignmentParam && this.categoryParameters.alignment) {
        const visibleOptions = this.getVisibleOptions(alignmentParam);
        const currentAlignmentAvailable = visibleOptions.some(opt => opt.value === this.categoryParameters.alignment);
        
        // Если текущее выравнивание недоступно, выбираем первое доступное
        if (!currentAlignmentAvailable && visibleOptions.length > 0) {
          this.categoryParameters.alignment = visibleOptions[0].value;
        }
      }
      
      // Проверяем, что contentRatio доступен (только для изображений)
      if (this.categoryParameters.mediaType === 'video' && this.categoryParameters.contentRatio === 'square') {
        this.categoryParameters.contentRatio = 'original';
      }
      
      this.updateFieldsAndCollection();
    },
    updateFieldsAndCollection() {
      if (!this.selectedCategory) return;
      
      // Определяем тип блока на основе параметров
      this.finalBlockType = this.selectedCategory.getBlockType(this.categoryParameters);
      
      // Получаем поля для выбранного типа
      if (this.selectedCategory.getFields) {
        this.currentFields = this.selectedCategory.getFields(this.categoryParameters);
      } else {
        this.currentFields = this.selectedCategory.fields || [];
      }
      
      // Получаем коллекцию если есть
      if (this.selectedCategory.getCollection) {
        this.currentCollection = this.selectedCategory.getCollection(this.categoryParameters);
        this.localCollection = [];
      } else {
        this.currentCollection = null;
        this.localCollection = [];
      }
      
      this.updateFieldValues();
      this.initializeButtonAction();
    },
    initializeFromExistingType(type) {
      // Пытаемся определить категорию для существующего типа
      const config = getBlockConfigByType(type);
      if (config) {
        // Находим подходящую категорию
        for (const category of this.blockCategories) {
          if (category.getBlockType) {
            // Пытаемся подобрать параметры
            const testParams = {};
            if (type.includes('text-block')) {
              testParams.alignment = type.includes('center') ? 'center' : 'left';
              testParams.hasButton = type.includes('button') ? 'link' : 'none';
            } else if (type.includes('hero')) {
              testParams.backgroundType = type.includes('img') ? 'image' : 'video';
              testParams.textAlignment = type.includes('center') ? 'center' : 'left';
              testParams.hasButton = type.includes('button') ? 'link' : 'none';
            } else if (type.includes('img-') || type.includes('text-left-') || type.includes('text-right-') || type.includes('text-top-') || type.includes('image-left-') || type.includes('image-right-') || type.includes('video-')) {
              // Определяем тип медиа на основе типа блока
              if (type.includes('video-')) {
                testParams.mediaType = 'video';
              } else {
                testParams.mediaType = 'image';
              }
              
              // Определяем layout для медиа блоков
              if (type.includes('text-left-video-right') || type.includes('text-left-image-right')) {
                testParams.layout = 'text-left-image-right';
              } else if (type.includes('video-left-text-right') || type.includes('image-left-text-right')) {
                testParams.layout = 'image-left-text-right';
              } else if (type.includes('text-right-video-left') || type.includes('text-right-image-left')) {
                testParams.layout = 'text-right-image-left';
              } else if (type.includes('text-top-video-bottom') || type.includes('text-top-image-bottom')) {
                testParams.layout = 'text-top-image-bottom';
              } else if (type.includes('video-square-block-text-side-right') || type.includes('img-square-block-text-side-right')) {
                testParams.layout = 'square-right';
              } else if (type.includes('video-rect-block-text-side-left') || type.includes('img-rect-block-text-side-left')) {
                testParams.layout = 'rect-left';
              } else if (type.includes('video-rect-block-text-side-right') || type.includes('img-rect-block-text-side-right')) {
                testParams.layout = 'rect-right';
              } else if (type.includes('video-rect-block-text-top-left') || type.includes('img-rect-block-text-top-left')) {
                testParams.layout = 'rect-top-left';
              } else if (type.includes('video-rect-block-text-top-both') || type.includes('img-rect-block-text-top-both')) {
                testParams.layout = 'rect-top-center';
              } else if (type.includes('video-rect-block-title-left-desc-right-video-bottom') || type.includes('img-rect-block-title-left-desc-right-image-bottom')) {
                testParams.layout = 'rect-title-left-desc-right';
              } else {
                testParams.layout = 'single';
              }
            } else if (type === 'image' || type === 'video') {
              testParams.mediaType = type;
              testParams.layout = 'single';
            }
            
            if (category.getBlockType(testParams) === type) {
              this.selectedCategoryId = category.id;
              this.selectedCategory = category;
              this.categoryParameters = testParams;
              this.finalBlockType = type;
              
              // Получаем поля для выбранного типа
              if (category.getFields) {
                this.currentFields = category.getFields(testParams);
              } else {
                this.currentFields = category.fields || [];
              }
              
              // Получаем коллекцию если есть
              if (category.getCollection) {
                this.currentCollection = category.getCollection(testParams);
              } else {
                this.currentCollection = null;
              }
              
              this.updateFieldValues();
              this.initializeButtonAction();
              break;
            }
          }
        }
      }
    },
    updateFieldValues() {
      if (!this.currentFields) {
        this.fieldValues = {};
        return;
      }

      const newFieldValues = {};
      const oldFieldValues = { ...this.fieldValues };
      
      this.currentFields.forEach(field => {
        // Проверяем, есть ли уже значение для этого поля
        const existingValue = this.getNestedValue(oldFieldValues, field.key);
        
        if (existingValue) {
          // Сохраняем существующее значение
          newFieldValues[field.key] = existingValue;
        } else {
          // Пытаемся получить из payload
          const value = this.getNestedValue(this.payload, field.key);
          newFieldValues[field.key] = value || '';
        }
        
        // Специальная обработка для переключения image_url <-> video_url
        if (field.key === 'image_url' && !newFieldValues[field.key]) {
          // Если переключились на изображение, а было видео - берем значение от video_url
          const videoValue = oldFieldValues['video_url'] || this.getNestedValue(oldFieldValues, 'video_url');
          const videoFileId = oldFieldValues['video_file_id'] || this.getNestedValue(oldFieldValues, 'video_file_id');
          if (videoValue) newFieldValues[field.key] = videoValue;
          if (videoFileId) newFieldValues['image_file_id'] = videoFileId;
        }
        
        if (field.key === 'video_url' && !newFieldValues[field.key]) {
          // Если переключились на видео, а было изображение - берем значение от image_url
          const imageValue = oldFieldValues['image_url'] || this.getNestedValue(oldFieldValues, 'image_url');
          const imageFileId = oldFieldValues['image_file_id'] || this.getNestedValue(oldFieldValues, 'image_file_id');
          if (imageValue) newFieldValues[field.key] = imageValue;
          if (imageFileId) newFieldValues['video_file_id'] = imageFileId;
        }
      });
      
      this.fieldValues = newFieldValues;
      
      // Инициализируем коллекции
      if (this.currentCollection) {
        const collectionKey = this.currentCollection.key;
        // Сохраняем существующую коллекцию если она есть
        if (this.localCollection && this.localCollection.length > 0) {
          // Оставляем как есть
        } else if (Array.isArray(this.payload[collectionKey])) {
          this.localCollection = [...this.payload[collectionKey]];
        } else {
          this.localCollection = [];
        }
      }
    },
    initializeButtonAction() {
      // Если параметр hasButton выбран, синхронизируем радио
      if (this.categoryParameters.hasButton === 'form') this.buttonActionType = 'form';
      if (this.categoryParameters.hasButton === 'link') this.buttonActionType = 'link';
      
      if (this.payload?.button?.action) {
        if (this.payload.button.action.openApplicationForm) {
          this.buttonActionType = 'form';
        } else if (this.payload.button.action.link) {
          this.buttonActionType = 'link';
          this.buttonLink = this.payload.button.action.link;
        }
      }
    },
    handleMediaUpload(fieldKey, response) {
      // Обрабатываем загруженный медиа файл для обычных и вложенных ключей
      // Сохраняем file_id вместо URL
      const fileId = response?.id;
      const fileName = response?.filename || response?.name || '';
      if (!fileId) return;
      
      // Автоматически определяем тип медиа по расширению файла
      if (fileName) {
        const extension = fileName.split('.').pop().toLowerCase();
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
        const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'mpeg', 'mpg'];
        
        if (imageExtensions.includes(extension) && this.categoryParameters.mediaType !== 'image') {
          this.categoryParameters.mediaType = 'image';
          this.onParameterChange();
        } else if (videoExtensions.includes(extension) && this.categoryParameters.mediaType !== 'video') {
          this.categoryParameters.mediaType = 'video';
          this.onParameterChange();
        }
      }
      
      // Если поле заканчивается на _url, заменяем на _file_id
      if (fieldKey.endsWith('_url')) {
        fieldKey = fieldKey.replace('_url', '_file_id');
      }
      
      this.setNestedValue(this.fieldValues, fieldKey, fileId);
    },
    handleCollectionMediaUpload(itemIndex, fieldKey, response) {
      // Обрабатываем загруженный медиа файл в коллекции
      // Сохраняем file_id вместо URL
      const fileId = response?.id;
      const fileName = response?.filename || response?.name || '';
      if (!fileId) return;
      
      // Автоматически определяем тип медиа по расширению файла для галерей
      if (fileName && this.selectedCategory?.id === 'gallery') {
        const extension = fileName.split('.').pop().toLowerCase();
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
        const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'mpeg', 'mpg'];
        
        if (imageExtensions.includes(extension) && this.categoryParameters.mediaType !== 'image') {
          this.categoryParameters.mediaType = 'image';
          this.onParameterChange();
        } else if (videoExtensions.includes(extension) && this.categoryParameters.mediaType !== 'video') {
          this.categoryParameters.mediaType = 'video';
          this.onParameterChange();
        }
      }
      
      if (this.localCollection[itemIndex]) {
        // Если поле заканчивается на _url, заменяем на _file_id
        if (fieldKey.endsWith('_url')) {
          fieldKey = fieldKey.replace('_url', '_file_id');
        }
        this.localCollection[itemIndex][fieldKey] = fileId;
      }
    },
    getNestedValue(obj, path) {
      return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj);
    },
    getValueByPath(obj, path) {
      return this.getNestedValue(obj, path);
    },
    setNestedValue(obj, path, value) {
      const keys = path.split('.');
      let ref = obj;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!ref[keys[i]] || typeof ref[keys[i]] !== 'object') {
          ref[keys[i]] = {};
        }
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
    },
    handleFieldInput(fieldKey, value) {
      // Устанавливаем значение через setNestedValue для поддержки вложенных ключей
      this.setNestedValue(this.fieldValues, fieldKey, value);
      // Сбрасываем флаг попытки сабмита при вводе
      if (this.attemptedSubmit) {
        this.attemptedSubmit = false;
      }
    },
    addCollectionItem() {
      const itemFields = this.currentCollection?.itemFields || [];
      const newItem = {};
      itemFields.forEach((f) => { newItem[f.key] = ''; });
      this.localCollection.push(newItem);
    },
    removeCollectionItem(idx) {
      this.localCollection.splice(idx, 1);
    },
    buildPayload() {
      if (!this.selectedCategory || !this.currentFields) return {};

      const out = { ...this.payload };
      
      // Обновляем payload из fieldValues (поддержка вложенных ключей)
      this.currentFields.forEach(field => {
        if (field.key === 'button.action') {
          // Специальная обработка для кнопок
          if (this.buttonActionType === 'link') {
            this.setNestedValue(out, 'button.action', { link: this.buttonLink });
          } else if (this.buttonActionType === 'form') {
            this.setNestedValue(out, 'button.action', { openApplicationForm: true });
          }
        } else {
          // Проверяем основное поле
          let value = this.getNestedValue(this.fieldValues, field.key);
          
          // Если поле заканчивается на _url и мы сохранили значение в _file_id, используем _file_id
          if (field.key.endsWith('_url')) {
            const fileIdKey = field.key.replace('_url', '_file_id');
            const fileIdValue = this.getNestedValue(this.fieldValues, fileIdKey);
            
            if (fileIdValue) {
              // Если есть загруженный файл, сохраняем его ID с суффиксом _file_id
              this.setNestedValue(out, fileIdKey, fileIdValue);
            } else if (value) {
              // Если есть URL (например, введённый вручную), сохраняем как есть
              this.setNestedValue(out, field.key, value);
            }
          } else {
            this.setNestedValue(out, field.key, value);
          }
        }
      });

      if (this.currentCollection) {
        // Обрабатываем элементы коллекции
        out[this.currentCollection.key] = this.localCollection.map((item) => {
          const processedItem = {};
          
          // Копируем все поля
          Object.keys(item).forEach(key => {
            if (key.endsWith('_file_id')) {
              // Для загруженных файлов сохраняем как есть
              processedItem[key] = item[key];
            } else if (key.endsWith('_url')) {
              // Если поле _url пустое, проверяем есть ли _file_id
              const fileIdKey = key.replace('_url', '_file_id');
              if (item[fileIdKey]) {
                // Если есть загруженный файл, используем file_id
                processedItem[fileIdKey] = item[fileIdKey];
              } else if (item[key]) {
                // Если есть URL (введенный вручную), сохраняем как есть
                processedItem[key] = item[key];
              }
            } else {
              // Обычные поля сохраняем как есть
              processedItem[key] = item[key];
            }
          });
          
          return processedItem;
        });
      }
      
      return out;
    },
    submit() {
      this.attemptedSubmit = true;
      
      if (!this.canSubmit || !this.isValidBlockType) {
        // Показываем алерт с перечислением проблемных полей
        if (!this.isValidBlockType) {
          alert(`❌ Некорректный тип блока: ${this.finalBlockType}\n\nЭтот тип блока не поддерживается системой.`);
          return;
        }
        
        if (this.missingFields.length > 0) {
          const fieldsList = this.missingFields.map(f => `• ${f.label}`).join('\n');
          alert(`❌ Заполните все обязательные поля:\n\n${fieldsList}`);
        }
        return;
      }

      if (!this.selectedCategory || !this.finalBlockType) return;

      const block = {
        type: this.finalBlockType,
        payload: this.buildPayload(),
      };
      if (this.localPosition != null) block.position = this.localPosition;
      this.$emit('submit', block);
    },
    isFieldMissing(fieldKey) {
      if (!this.attemptedSubmit) return false;
      return this.missingFields.some(f => f.key === fieldKey);
    }
  }
};
</script>

<style scoped>
.dark-icon {
  filter: brightness(0) saturate(100%) invert(0);
}

.dark .dark-icon {
  filter: invert(1);
}
</style>


